import {
  mdiDesk, mdiDoorOpen, mdiFormatText, mdiTextureBox, mdiVectorSquareEdit,
} from '@mdi/js';
import type { DrawLayerTag } from '../utils/draw-plan';
import { editorTheme } from '../utils/editor-theme';
import type { EditorFeature, PolygonLayer } from './editor-feature';

/**
 * What the admin is working on.
 *
 * The simple editor is organised around the things on a floorplan rather than around drawing
 * operations. Somebody who wants to make a meeting room bigger thinks "rooms", not "vertex
 * tool on the room layer" — so they pick Rooms, click the room, and every change that can be
 * made to a room is laid out in the panel beside it.
 *
 * A mode narrows what a click can reach and fades everything else, so the plan itself shows
 * which shapes are in play. `select` is the unrestricted mode: anything can be clicked.
 */
export type EditMode = 'select' | 'rooms' | 'floor' | 'border' | 'desks' | 'labels';

export const EDIT_MODES: readonly EditMode[] = ['select', 'rooms', 'floor', 'border', 'desks', 'labels'];

/** The polygon layer a mode edits, for the three modes that edit one. */
export const polygonLayerOf = (mode: EditMode): PolygonLayer | null => {
  switch (mode) {
    case 'rooms':
      return 'room';
    case 'floor':
      return 'floor';
    case 'border':
      return 'border';
    default:
      return null;
  }
};

/** Whether a mode edits drawn areas — rooms, the floor, the outline — rather than markers. */
export const isAreaMode = (mode: EditMode): boolean => polygonLayerOf(mode) !== null;

/** Whether a click in this mode may land on the feature. */
export const acceptsFeature = (mode: EditMode, feature: EditorFeature): boolean => {
  const layer = polygonLayerOf(mode);

  if (layer) {
    return feature.kind === 'polygon' && feature.layer === layer;
  }

  switch (mode) {
    case 'desks':
      // The furniture outline is part of the desk as far as anybody placing desks is concerned.
      return feature.kind === 'desk' || feature.kind === 'deskShape';
    case 'labels':
      return feature.kind === 'textLabel' || feature.kind === 'iconLabel';
    default:
      return true;
  }
};

const ALL_LAYERS: readonly DrawLayerTag[] = ['floor', 'room', 'border', 'deskShape', 'desk', 'label'];

/** The layers each mode leaves at full strength. Everything else is drawn faded. */
const FOCUSED: Readonly<Record<EditMode, readonly DrawLayerTag[]>> = {
  select: ALL_LAYERS,
  rooms: ['room'],
  floor: ['floor'],
  border: ['border'],
  // Rooms stay clear behind desks: which room a desk sits in is the question desk placement
  // turns on, and fading it would hide the answer.
  desks: ['room', 'deskShape', 'desk'],
  labels: ['floor', 'room', 'border', 'label'],
};

export const dimmedLayersOf = (mode: EditMode): ReadonlySet<DrawLayerTag> => {
  const focused = new Set(FOCUSED[mode]);
  return new Set(ALL_LAYERS.filter((layer) => !focused.has(layer)));
};

const BADGE_ICONS: Readonly<Record<Exclude<EditMode, 'select'>, string>> = {
  rooms: mdiDoorOpen,
  floor: mdiTextureBox,
  border: mdiVectorSquareEdit,
  desks: mdiDesk,
  labels: mdiFormatText,
};

/**
 * The pointer for a mode: an arrow with the mode's icon in a badge beside it.
 *
 * What tells the admin, without reading anything, that the next click edits a room and not
 * whatever else is under the pointer. Undefined for `select`, which keeps the plain pointer.
 *
 * Drawn as an inline SVG so it needs no asset and no request, with the ordinary arrow as the
 * fallback for a browser that will not take a custom cursor.
 */
export const modeCursorOf = (mode: EditMode): string | undefined => {
  if (mode === 'select') {
    return undefined;
  }

  const svg = [
    '<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">',
    '<path d="M3 2 L3 21 L8 16.5 L11.5 24 L14.5 22.6 L11 15.3 L17.5 15.3 Z" ',
    'fill="#ffffff" stroke="#1B2230" stroke-width="1.4" stroke-linejoin="round"/>',
    `<circle cx="23" cy="23" r="8.5" fill="${editorTheme.accent}" stroke="#ffffff" stroke-width="1.5"/>`,
    `<path d="${BADGE_ICONS[mode]}" fill="#ffffff" transform="translate(16.4 16.4) scale(0.55)"/>`,
    '</svg>',
  ].join('');

  return `url("data:image/svg+xml,${encodeURIComponent(svg)}") 3 2, default`;
};
